import React, { useState, useCallback } from 'react';
import { Dispatch } from 'redux';
import Paper from '@mui/material/Paper';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Checkbox, FormControlLabel } from '@mui/material';
import { connectWithRouter } from '../../helpers/connectWithRouter';
import { hasPermission } from '../../helpers/Permission';
import { PermissionTypes } from '../../constants';
import { API_CALL } from '../../configs/ActionTypes';
import { enqueueSnackbarCustomMessageHandler } from '../../actions/Status';
import { getSSOValidationError } from '../../helpers/SSOValidation';
import SSOUpload, { SSOFormHandle } from './SSOUploadUSB';

const styles = require('./styles.pcss');

type OwnProps = {
  organizationId: string;
};

type Props = OwnProps & { dispatch: Dispatch };

const SSOIntegrationSetup: React.FC<Props> = ({
  organizationId,
  dispatch,
}) => {
  if (!hasPermission('features', PermissionTypes.update)) {
    return (
      <Paper sx={{ borderRadius: '8px', marginBottom: '2rem' }}>
        <CardHeader title="Add SAML Integration" />
        <CardContent>
          <Typography color="error">
            Insufficient permissions. Contact your administrator to configure SSO integrations.
          </Typography>
        </CardContent>
      </Paper>
    );
  }

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [enableProvisioning, setEnableProvisioning] = useState<boolean>(false);
  const [enforceRedirect, setEnforceRedirect] = useState<boolean>(false);
  const [disableRequestedAuthnContext, setDisableRequestedAuthnContext] = useState<boolean>(false);
  const [ssoFormHandle, setSsoFormHandle] = useState<SSOFormHandle | null>(null);

  const handleEnableProvisioning = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setEnableProvisioning(e.target.checked),
    []
  );

  const handleEnforceRedirect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setEnforceRedirect(e.target.checked),
    []
  );

  const handleDisableRequestedAuthnContext = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setDisableRequestedAuthnContext(e.target.checked),
    []
  );

  const handleSubmit = useCallback(() => {
    if (!ssoFormHandle) {
      enqueueSnackbarCustomMessageHandler(dispatch, 'Form initialization error', 'error');
      return;
    }

    const formData = ssoFormHandle.getFormData();
    const validationError = getSSOValidationError(
      formData.entrypoint,
      formData.issuer,
      formData.cert
    );
    
    if (!ssoFormHandle.validateAndMarkErrors()) {
      const errorMessage = validationError || 'Validation failed';
      enqueueSnackbarCustomMessageHandler(dispatch, errorMessage, 'error');
      return;
    }

    setIsLoading(true);

    const data = {
      organizationId,
      settings: {
        cert: formData.cert,
        entryPoint: formData.entrypoint,
        issuer: formData.issuer,
        enableProvisioning,
        idpDisplayName: formData.idpDisplayName,
        enforceRedirect,
        disableRequestedAuthnContext,
      },
    };

    dispatch({
      type: API_CALL,
      payload: {
        call: 'integrations/sso',
        method: 'post',
        data: JSON.stringify(data),
        success: (response: any) => {
          setIsLoading(false);
          enqueueSnackbarCustomMessageHandler(dispatch, 'Integration added successfully!', 'success');
          if (ssoFormHandle?.resetForm) {
            ssoFormHandle.resetForm();
          }
          setEnableProvisioning(false);
          setEnforceRedirect(false);
          setDisableRequestedAuthnContext(false);
        },
        fail: (error: any) => {
          enqueueSnackbarCustomMessageHandler(dispatch, error?.payload?.message || 'API error occurred', 'error');
          setIsLoading(false);
        },
      },
    });
  }, [dispatch, organizationId, enableProvisioning, enforceRedirect, disableRequestedAuthnContext, ssoFormHandle]);

  return (
    <Paper
      sx={{
        borderRadius: '8px',
        marginBottom: '2rem',
      }}
    >
      <CardHeader
        title="Add SAML Integration"
        sx={{
          borderBottom: '1px solid #eee',
          '& .MuiCardHeader-title': {
            fontSize: '1.3rem',
          },
        }}
      />
      <CardContent>
        <SSOUpload onFormReady={setSsoFormHandle} />
        <FormControlLabel
          control={
            <Checkbox
              checked={enableProvisioning}
              onChange={handleEnableProvisioning}
              color="primary"
            />
          }
          label="Enable Provisioning"
          className={styles.toggle}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={enforceRedirect}
              onChange={handleEnforceRedirect}
              color="primary"
            />
          }
          label="Redirect on TravelBank login (This is off by default and will allow organizations to test their integration, once enabled all users whose email domains match the organization will be redirected to SSO"
          className={styles.toggle}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={disableRequestedAuthnContext}
              onChange={handleDisableRequestedAuthnContext}
              color="primary"
            />
          }
          label="Mobile Override"
          className={styles.toggle}
        />
        <div className={styles.buttonContainer}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isLoading}
            sx={{ position: 'relative' }}
          >
            {isLoading ? 'Adding Integration...' : 'Add Integration'}
          </Button>
        </div>
      </CardContent>
    </Paper>
  );
};

function mapStateToProps() {
  return {};
}

function mapDispatchToProps(dispatch: Dispatch) {
  return { dispatch };
}

export { SSOIntegrationSetup };

export default connectWithRouter(mapStateToProps, mapDispatchToProps)(SSOIntegrationSetup) as React.FC<OwnProps>;
